import React, { useState } from 'react';
import {
    collection, query, where, limit, getDocs, getDoc, doc,
    updateDoc, arrayUnion, Timestamp,
} from 'firebase/firestore';
import { Star, X, Save, CheckCircle2 } from 'lucide-react';
import { db } from './firebase';

/** ৫টা star, half-star সহ। onPick দিলে ক্লিক করা যায় (বাম অর্ধেক = .5, ডান অর্ধেক = পূর্ণ)। */
function Stars({ value, size = 16, onPick }) {
    return (
        <span className="inline-flex">
            {[1, 2, 3, 4, 5].map((i) => {
                const fill = value >= i ? 100 : value >= i - 0.5 ? 50 : 0;
                return (
                    <span key={i} className="relative inline-block" style={{ width: size, height: size }}>
                        <Star size={size} className="text-gray-300" />
                        <span className="absolute inset-y-0 left-0 overflow-hidden" style={{ width: `${fill}%` }}>
                            <Star size={size} className="fill-amber-400 text-amber-400" />
                        </span>
                        {onPick && (
                            <>
                                <button type="button" className="absolute inset-y-0 left-0 w-1/2" onClick={() => onPick(i - 0.5)} />
                                <button type="button" className="absolute inset-y-0 right-0 w-1/2" onClick={() => onPick(i)} />
                            </>
                        )}
                    </span>
                );
            })}
        </span>
    );
}

/**
 * Product document খোঁজা: cartItems এ যে productId আছে সেটা products collection এর
 * `productId` ফিল্ডের সাথে মেলে। না পেলে document id হিসেবেও চেষ্টা করে।
 */
async function findProductRef(productId) {
    if (!productId) return null;
    const snap = await getDocs(query(collection(db, 'products'), where('productId', '==', productId), limit(1)));
    if (!snap.empty) return snap.docs[0].ref;
    const direct = doc(db, 'products', String(productId));
    const directSnap = await getDoc(direct);
    return directSnap.exists() ? direct : null;
}

/**
 * Order এর product এর জন্য সরাসরি রিভিউ যোগ করে।
 * রিভিউ সেই product এর `reviews` array তে যায় (Product Manager এর সাথে একই format),
 * আর order এর ওই cartItem এ review_added = true হয়ে যায়।
 */
export default function AddReviewModal({ order, onClose, onSaved }) {
    const items = order.cartItems || [];
    const [itemIndex, setItemIndex] = useState(() => {
        const firstPending = items.findIndex((it) => !it.review_added);
        return firstPending === -1 ? 0 : firstPending;
    });
    const [name, setName] = useState(order.customerInfo?.name || '');
    const [phone, setPhone] = useState(order.customerInfo?.phone || '');
    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const item = items[itemIndex];

    const save = async () => {
        setError('');
        if (!item) return setError('এই অর্ডারে কোনো product নেই');
        if (!name.trim() || !phone.trim() || !comment.trim() || rating === 0) return setError('সব ফিল্ড পূরণ করুন');
        if (phone.replace(/\D/g, '').length < 10) return setError('সঠিক ফোন নম্বর দিন');

        setBusy(true);
        try {
            const productRef = await findProductRef(item.productId);
            if (!productRef) throw new Error('Product খুঁজে পাওয়া যায়নি (productId: ' + (item.productId || 'N/A') + ')');

            const review = {
                reviewId: (window.crypto && window.crypto.randomUUID)
                    ? window.crypto.randomUUID()
                    : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
                userName: name.trim(),
                phone: phone.trim(),
                rating,
                comment: comment.trim(),
                timestamp: Timestamp.now(),
                active: true,
            };
            await updateDoc(productRef, { reviews: arrayUnion(review) });

            // order এর ওই cartItem এ review_added = true (আগে সর্বশেষ order টা পড়ে নিই)
            const orderRef = doc(db, 'orders', order.id);
            const orderSnap = await getDoc(orderRef);
            const latest = (orderSnap.exists() ? orderSnap.data().cartItems : items) || [];
            const idx = latest[itemIndex]?.productId === item.productId
                ? itemIndex
                : latest.findIndex((x) => x.productId === item.productId);
            const nextItems = latest.map((it, i) => (i === idx ? { ...it, review_added: true } : it));
            await updateDoc(orderRef, { cartItems: nextItems });

            onSaved(order.id, nextItems);
            onClose();
        } catch (e) {
            console.error('Error adding review:', e);
            setError(e.message || 'রিভিউ যোগ করা যায়নি');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl p-6 w-full max-w-lg border border-gray-200 max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-2xl font-bold text-gray-900">Add Review</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-900"><X size={24} /></button>
                </div>

                <div className="mb-4 p-4 bg-gray-50 rounded-lg text-sm text-gray-700">
                    <p>Order ID: <span className="font-mono text-orange-600">{order.orderId}</span></p>
                    <p>Customer: <span className="font-semibold">{order.customerInfo?.name || 'N/A'}</span></p>
                </div>

                {/* Product select */}
                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Product</label>
                    <div className="space-y-2">
                        {items.map((it, i) => (
                            <button
                                type="button"
                                key={`${it.productId}-${i}`}
                                onClick={() => setItemIndex(i)}
                                className={`w-full flex items-center gap-3 p-2 rounded-lg border text-left transition
                                    ${i === itemIndex ? 'border-orange-400 bg-orange-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
                            >
                                <img
                                    src={it.image}
                                    alt={it.name}
                                    className="w-10 h-10 rounded object-cover"
                                    onError={(e) => { e.target.style.visibility = 'hidden'; }}
                                />
                                <span className="flex-1 min-w-0 text-sm font-medium text-gray-900 truncate">{it.name}</span>
                                {it.review_added && (
                                    <span className="flex items-center gap-1 text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                                        <CheckCircle2 size={12} /> Reviewed
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                    {item?.review_added && (
                        <p className="mt-2 text-xs text-amber-700">এই product এ এই order থেকে আগেই রিভিউ যোগ করা হয়েছে। আবার দিলে আরেকটা রিভিউ যোগ হবে।</p>
                    )}
                </div>

                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">ব্যবহারকারীর নাম</label>
                        <input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="নাম লিখুন..."
                            className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-400"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">ফোন নম্বর</label>
                        <input
                            type="tel"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder="01XXXXXXXXX"
                            className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-400"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">রেটিং</label>
                        <div className="flex items-center gap-2">
                            <Stars value={rating} size={28} onPick={setRating} />
                            <span className="text-sm text-gray-500">{rating ? rating.toFixed(1) : ''}</span>
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">মন্তব্য</label>
                        <textarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder="মন্তব্য লিখুন..."
                            className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-400 h-28 resize-none"
                        />
                    </div>
                </div>

                {error && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">{error}</div>
                )}

                <button
                    onClick={save}
                    disabled={busy}
                    className="mt-5 w-full flex items-center justify-center gap-2 bg-orange-400 hover:bg-orange-500 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <Save size={20} />
                    {busy ? 'Saving...' : 'Add Review'}
                </button>
            </div>
        </div>
    );
}
